// path: src/Friends.js
// Phase B: GID identity + send/accept/deny/cancel/unfriend.

import React, { useEffect, useState } from "react";
import Login from "./Login";
import { formatGid, isValidGid } from "./utils/gid";
import { validateGidInput } from "./utils/friends";
import "./App.css";

const TABS = [
    { id: "friends", label: "Friends" },
    { id: "incoming", label: "Incoming" },
    { id: "outgoing", label: "Outgoing" },
];

const Friends = ({
    isAuthenticated = false,
    user = null,
    gid = null,
    gidStatus = "idle",
    gidError = "",
    incomingRequests = [],
    outgoingRequests = [],
    friends = [],
    friendsLoading = false,
    friendsError = "",
    actionMessage = "",
    actionError = "",
    busyActionKey = "",
    onLoginSuccess,
    onRetryGid,
    onRefreshFriends,
    onSendFriendRequest,
    onAcceptFriendRequest,
    onDenyFriendRequest,
    onCancelFriendRequest,
    onUnfriend,
    onViewFriendProfile,
}) => {
    const [copied, setCopied] = useState(false);
    const [tab, setTab] = useState("friends");
    const [gidDraft, setGidDraft] = useState("");
    const [localAddError, setLocalAddError] = useState("");
    const hasGid = isValidGid(gid);
    const gidText = hasGid ? formatGid(gid) : "";

    useEffect(() => {
        if (isAuthenticated && user?.uid && typeof onRefreshFriends === "function") {
            onRefreshFriends();
        }
    }, [isAuthenticated, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopyGid = async () => {
        if (!gidText || !navigator?.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText(gidText);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        } catch (error) {
            console.error("Failed to copy GID:", error);
        }
    };

    const handleSend = async (event) => {
        event.preventDefault();
        setLocalAddError("");

        const parsed = validateGidInput(gidDraft);
        if (!parsed.ok) {
            setLocalAddError(parsed.error);
            return;
        }

        if (hasGid && parsed.gid === Number(gid)) {
            setLocalAddError("You cannot send a friend request to yourself.");
            return;
        }

        if (typeof onSendFriendRequest === "function") {
            const ok = await onSendFriendRequest(parsed.gid);
            if (ok) {
                setGidDraft("");
                setTab("outgoing");
            }
        }
    };

    const renderRequestRow = (request, mode) => {
        const isIncoming = mode === "incoming";
        const labelName = isIncoming
            ? request.fromUsername || "Player"
            : request.toUsername || "Player";
        const labelGid = formatGid(isIncoming ? request.fromGid : request.toGid);
        const acceptKey = `accept:${request.id}`;
        const denyKey = `deny:${request.id}`;
        const cancelKey = `cancel:${request.id}`;

        return (
            <div className="friendsListRow" key={request.id}>
                <div className="friendsListMeta">
                    <div className="friendsListName">{labelName}</div>
                    <div className="friendsListSub">GID {labelGid || "--------"}</div>
                </div>
                <div className="friendsListActions">
                    {isIncoming ? (
                        <>
                            <button
                                type="button"
                                className="profileSmallButton"
                                disabled={Boolean(busyActionKey)}
                                onClick={() => onAcceptFriendRequest(request.id)}
                            >
                                {busyActionKey === acceptKey ? "…" : "Accept"}
                            </button>
                            <button
                                type="button"
                                className="profileSmallButton profileSmallButton--secondary"
                                disabled={Boolean(busyActionKey)}
                                onClick={() => onDenyFriendRequest(request.id)}
                            >
                                {busyActionKey === denyKey ? "…" : "Deny"}
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="profileSmallButton profileSmallButton--secondary"
                            disabled={Boolean(busyActionKey)}
                            onClick={() => onCancelFriendRequest(request.id)}
                        >
                            {busyActionKey === cancelKey ? "…" : "Cancel"}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderFriendsList = () => {
        if (friendsLoading) {
            return <div className="friendsBody">Loading friends…</div>;
        }

        if (!friends.length) {
            return (
                <div className="friendsBody">
                    No friends yet. Send a request using someone’s GID.
                </div>
            );
        }

        return friends.map((friend) => {
            const unfriendKey = `unfriend:${friend.uid}`;
            const canOpen =
                typeof onViewFriendProfile === "function" && friend.uid;

            return (
                <div className="friendsListRow" key={friend.uid}>
                    <div className="friendsListMeta">
                        {canOpen ? (
                            <button
                                type="button"
                                className="friendsListName friendsListName--link"
                                onClick={() => onViewFriendProfile(friend.uid)}
                            >
                                {friend.username || "Player"}
                            </button>
                        ) : (
                            <div className="friendsListName">
                                {friend.username || "Player"}
                            </div>
                        )}
                        <div className="friendsListSub">
                            GID {formatGid(friend.gid) || "--------"}
                        </div>
                    </div>
                    <div className="friendsListActions">
                        {canOpen ? (
                            <button
                                type="button"
                                className="profileSmallButton"
                                disabled={Boolean(busyActionKey)}
                                onClick={() => onViewFriendProfile(friend.uid)}
                            >
                                View
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="profileSmallButton profileSmallButton--secondary"
                            disabled={Boolean(busyActionKey)}
                            onClick={() => onUnfriend(friend.uid)}
                        >
                            {busyActionKey === unfriendKey ? "…" : "Unfriend"}
                        </button>
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="friendsScreen">
            <div className="friendsHeader">
                <h1 className="impactFont pageTitle friendsTitle">Friends</h1>
            </div>

            <div className="friendsPanel">
                {!isAuthenticated || !user ? (
                    <>
                        <h2 className="friendsSectionTitle">Sign in required</h2>
                        <p className="friendsBody">
                            Friends are only available on a signed-in account.
                            Guest play cannot send or receive friend requests.
                        </p>
                        <div className="friendsActions">
                            <Login onSuccess={onLoginSuccess} />
                        </div>
                    </>
                ) : (
                    <>
                        <h2 className="friendsSectionTitle">Your Game Identity</h2>
                        <p className="friendsBody">
                            Share this 8-digit GID so friends can find you.
                        </p>

                        <div className="friendsGidCard">
                            {hasGid ? (
                                <>
                                    <div className="friendsGidLabel">GID</div>
                                    <div className="friendsGidValue">{gidText}</div>
                                    <button
                                        type="button"
                                        className="profileSmallButton"
                                        onClick={handleCopyGid}
                                    >
                                        {copied ? "Copied" : "Copy GID"}
                                    </button>
                                </>
                            ) : gidStatus === "loading" ? (
                                <div className="friendsBody">
                                    Setting up your Game Identity…
                                </div>
                            ) : (
                                <>
                                    <div className="friendsBody friendsBody--error">
                                        {gidError ||
                                            "Game Identity is not ready yet. Deploy Cloud Functions if this persists."}
                                    </div>
                                    {typeof onRetryGid === "function" && (
                                        <button
                                            type="button"
                                            className="profileSmallButton"
                                            onClick={onRetryGid}
                                        >
                                            Try Again
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <h2 className="friendsSectionTitle">Add Friend</h2>
                        <form className="friendsAddForm" onSubmit={handleSend}>
                            <input
                                className="friendsGidInput"
                                value={gidDraft}
                                onChange={(event) =>
                                    setGidDraft(event.target.value.replace(/\D/g, "").slice(0, 8))
                                }
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={8}
                                placeholder="8-digit GID"
                                aria-label="Friend GID"
                                disabled={!hasGid || Boolean(busyActionKey)}
                            />
                            <button
                                type="submit"
                                className="homePrimaryButton"
                                disabled={!hasGid || Boolean(busyActionKey)}
                            >
                                {busyActionKey === "send" ? "Sending…" : "Send Request"}
                            </button>
                        </form>
                        {(localAddError || actionError) && (
                            <div className="friendsBody friendsBody--error">
                                {localAddError || actionError}
                            </div>
                        )}
                        {actionMessage && !localAddError && (
                            <div className="friendsBody friendsBody--ok">
                                {actionMessage}
                            </div>
                        )}

                        <div className="friendsTabs" role="tablist" aria-label="Friends sections">
                            {TABS.map((item) => {
                                const count =
                                    item.id === "incoming"
                                        ? incomingRequests.length
                                        : item.id === "outgoing"
                                        ? outgoingRequests.length
                                        : friends.length;

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={tab === item.id}
                                        className={`friendsTab ${
                                            tab === item.id ? "friendsTab--active" : ""
                                        }`}
                                        onClick={() => setTab(item.id)}
                                    >
                                        {item.label}
                                        <span className="friendsTabCount">{count}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {friendsError && (
                            <div className="friendsBody friendsBody--error">
                                {friendsError}
                            </div>
                        )}

                        <div className="friendsList">
                            {tab === "friends" && renderFriendsList()}
                            {tab === "incoming" &&
                                (incomingRequests.length
                                    ? incomingRequests.map((request) =>
                                          renderRequestRow(request, "incoming")
                                      )
                                    : (
                                          <div className="friendsBody">
                                              No incoming requests.
                                          </div>
                                      ))}
                            {tab === "outgoing" &&
                                (outgoingRequests.length
                                    ? outgoingRequests.map((request) =>
                                          renderRequestRow(request, "outgoing")
                                      )
                                    : (
                                          <div className="friendsBody">
                                              No outgoing requests.
                                          </div>
                                      ))}
                        </div>

                        <div className="friendsHint">
                            Signed in as {user.email || "your account"}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Friends;
